import { Box } from "../../components/ui/box";
import { DrawerPanel, DrawerPopup } from "../../components/ui/drawer";
import { PopoverPopup } from "../../components/ui/popover";
import { cn } from "../../lib/utils";
import * as React from "react";
import { useEntitySelector } from "./entity-selector-context";
import { EntitySelectorEmpty } from "./entity-selector-empty";
import type { Omittable } from "../../lib/props";

const DEFAULT_MAX_HEIGHT = 400;

// Min-width presets so entity selectors and searchable selects read at the
// same width side by side. Capped to the viewport minus a 1rem gutter per side.
export type EntitySelectorContentSize = "sm" | "md" | "lg";

const CONTENT_MIN_WIDTH_CLASSES: Record<EntitySelectorContentSize, string> = {
  sm: "min-w-[min(320px,calc(100vw-2rem))]",
  md: "min-w-[min(360px,calc(100vw-2rem))]",
  lg: "min-w-[min(400px,calc(100vw-2rem))]",
};

// Drawer mode pins the search input (and pool tabs) above the one scrollable
// panel — a scrolling search field on a touch drawer is unusable. The split is
// declarative: these parts render in the fixed header region, everything else
// (selected rows, pools, footer) scrolls inside the DrawerPanel.
const DRAWER_HEADER_DISPLAY_NAMES = new Set([
  "EntitySelectorInput",
  "EntitySelectorTabs",
  "EntitySelectorTabsList",
  "EntitySelectorAutoTabs",
]);

// Every selector gets an empty state whether or not the call site remembered to
// render one — a popup showing a search field above nothing at all is the
// failure this replaces, and it is the default a consumer never opts into. An
// explicit `EntitySelectorEmpty` anywhere in the children wins; the fallback
// only fills a gap.
//
// "Anywhere" means through ANY wrapper, not just a Fragment. Nearly every call
// site wraps its parts in a `Box` for the max-height/overflow column, so a scan
// that only descended into Fragments missed the explicit empty state and
// appended the fallback beside it — the popup then showed BOTH messages ("No
// events found" over "No results found").
//
// The limit worth knowing: this walks the element tree the consumer passed as
// JSX. An `EntitySelectorEmpty` returned from inside a consumer's OWN component
// is not visible here, so render it in this tree rather than hiding it behind a
// wrapper component.
function hasEmptyChild(children: React.ReactNode): boolean {
  let found = false;
  React.Children.forEach(children, (child) => {
    if (found || !React.isValidElement(child)) return;
    if (displayNameOf(child) === "EntitySelectorEmpty") {
      found = true;
      return;
    }
    const nested = (child.props as { children?: React.ReactNode }).children;
    if (nested != null) found = hasEmptyChild(nested);
  });
  return found;
}

function displayNameOf(child: React.ReactElement): string | undefined {
  return (child.type as { displayName?: string })?.displayName;
}

function withEmptyState(children: React.ReactNode): React.ReactNode {
  if (hasEmptyChild(children)) return children;
  const list = React.Children.toArray(children);
  // Ahead of the footer, which is the popup's last row and sits below the list.
  const footerIndex = list.findIndex(
    (child) =>
      React.isValidElement(child) &&
      displayNameOf(child) === "EntitySelectorFooter",
  );
  const fallback = <EntitySelectorEmpty key="entity-selector-empty" />;
  return footerIndex === -1
    ? [...list, fallback]
    : [...list.slice(0, footerIndex), fallback, ...list.slice(footerIndex)];
}

function partitionDrawerChildren(children: React.ReactNode): {
  header: React.ReactNode[];
  panel: React.ReactNode[];
} {
  const header: React.ReactNode[] = [];
  const panel: React.ReactNode[] = [];
  React.Children.forEach(children, (child) => {
    const displayName = React.isValidElement(child)
      ? displayNameOf(child)
      : undefined;
    if (displayName && DRAWER_HEADER_DISPLAY_NAMES.has(displayName)) {
      header.push(child);
    } else {
      panel.push(child);
    }
  });
  return { header, panel };
}

export type EntitySelectorContentProps = Omittable<{
  side?: "top" | "bottom";
  align?: "start" | "center" | "end";
  maxHeight?: number;
  /** Maximum width of the content area in pixels. Omit to let the popover (or `fit`/`size` preset) drive width. */
  maxWidth?: number;
  /** Minimum height for the content area. Prevents the popover from collapsing when results are empty. */
  minHeight?: number;
  /** Minimum width preset. @default 'lg' */
  size?: EntitySelectorContentSize;
  /**
   * Floors the content at the trigger's width. It is a MINIMUM, not a match:
   * pinning the popup to a narrow trigger truncates every row and the search
   * field with it.
   * @default false
   */
  fit?: boolean;
  ref?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}>;

function EntitySelectorContent({
  side = "bottom",
  align = "start",
  maxHeight = DEFAULT_MAX_HEIGHT,
  maxWidth,
  minHeight,
  size,
  fit = false,
  ref,
  children,
}: EntitySelectorContentProps) {
  const { variant } = useEntitySelector();

  const body = withEmptyState(children);

  const constraintStyle = React.useMemo(
    () => ({
      maxHeight,
      ...(maxWidth != null && !fit && { maxWidth }),
      ...(minHeight != null && { minHeight }),
    }),
    [maxHeight, maxWidth, minHeight, fit],
  );

  // Inline mode: no popover/drawer wrapper — the host (a submenu popup, a
  // panel) supplies the chrome. The content still owns its own width floor and
  // height cap; without them the host's tiny min-width (a menu's 128px) sizes
  // the whole selector and the pool overflows the host instead of scrolling.
  if (variant === "inline") {
    return (
      <Box
        direction="column"
        overflow="hidden"
        style={constraintStyle}
        className={cn(
          size && CONTENT_MIN_WIDTH_CLASSES[size],
          // Submenus want the narrow preset by default; pass `size` to widen.
          !size && CONTENT_MIN_WIDTH_CLASSES.sm,
        )}
      >
        {body}
      </Box>
    );
  }

  // Drawer mode: the popup hosts a fixed header region (search input + tabs)
  // above the DrawerPanel — the panel's ScrollArea is the ONE scrollable, so
  // pools render their rows flat inside it (no nested scroller).
  if (variant === "drawer") {
    const { header, panel } = partitionDrawerChildren(body);
    return (
      <DrawerPopup showBar>
        {header.length > 0 && <Box direction="column">{header}</Box>}
        <DrawerPanel className="p-0">
          <Box direction="column">{panel}</Box>
        </DrawerPanel>
      </DrawerPopup>
    );
  }

  // Desktop: render popover with consumer-provided children. The selector list
  // owns its padding and scroll, so the popup carries none.
  return (
    <PopoverPopup
      ref={ref}
      side={side}
      align={align}
      padding="none"
      className={cn(
        size && CONTENT_MIN_WIDTH_CLASSES[size],
        !size && !fit && CONTENT_MIN_WIDTH_CLASSES.lg,
        fit && "min-w-(--anchor-width)",
      )}
    >
      <Box fill="width"
        direction="column"
        overflow="hidden"
        style={constraintStyle}
      >
        {body}
      </Box>
    </PopoverPopup>
  );
}

EntitySelectorContent.displayName = "EntitySelectorContent";

export { EntitySelectorContent };
